import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Box,
  Typography,
  Divider,
  Chip,
  InputAdornment,
  Paper,
  CircularProgress
} from '@mui/material';
import {
  Search as SearchIcon,
  StarOutlined as StarIcon,
  TextSnippetOutlined as SnippetIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { createApiUrl } from '../../utils/apiConfig';
import { getAuthHeaders } from '../../utils/authUtils';
import { useSnackbar } from '../CentralSnackbar';

const SnippetSelector = ({ 
  open, 
  onClose, 
  onSnippetSelect, 
  anchorEl,
  cursorPosition 
}) => {
  const { t } = useTranslation();
  const { showError } = useSnackbar();
  const [searchTerm, setSearchTerm] = useState('');
  const [mostUsedSnippets, setMostUsedSnippets] = useState([]);
  const [allSnippets, setAllSnippets] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const listRef = useRef(null);
  const searchRef = useRef(null);

  // Load data when dialog opens
  useEffect(() => {
    if (open) {
      loadSnippets();
      // Focus search field immediately with multiple attempts
      const focusSearchField = () => {
        if (searchRef.current) {
          // For Material-UI TextField, we need to access the input element
          const inputElement = searchRef.current.querySelector('input');
          if (inputElement) {
            inputElement.focus();
            inputElement.select();
          } else {
            // Fallback for direct focus
            searchRef.current.focus();
          }
        }
      };
      
      // Try immediately
      focusSearchField();
      
      // Also try after a short delay to ensure DOM is ready
      setTimeout(focusSearchField, 10);
      
      // Final attempt after dialog animation
      setTimeout(focusSearchField, 150);
    } else {
      // Reset when closed
      setSearchTerm('');
      setSearchResults([]);
      setSelectedIndex(0);
    }
  }, [open]);

  // Search when term changes
  useEffect(() => {
    if (searchTerm.trim()) {
      performSearch(searchTerm);
    } else {
      setSearchResults([]);
      setSelectedIndex(0);
    }
  }, [searchTerm]);

  const loadSnippets = async () => {
    setLoading(true);
    try {
      // Load most used snippets (top 3)
      const mostUsedResponse = await fetch(createApiUrl('snippets/most-used?limit=3'), {
        headers: { ...getAuthHeaders() }
      });
      if (mostUsedResponse.ok) {
        const mostUsedData = await mostUsedResponse.json();
        setMostUsedSnippets(mostUsedData);
      }

      // Load all available snippets
      const allSnippetsResponse = await fetch(createApiUrl('snippets/for-user'), {
        headers: { ...getAuthHeaders() }
      });
      if (allSnippetsResponse.ok) {
        const allSnippetsData = await allSnippetsResponse.json();
        setAllSnippets(allSnippetsData);
      } else {
        showError(t('report.snippets.loadError'), 'ERR-SNIPPET-001');
      }
    } catch (error) {
      console.error('Error loading snippets:', error);
      showError(t('report.snippets.loadError'), 'ERR-SNIPPET-002');
    } finally {
      setLoading(false);
    }
  };

  const performSearch = async (term) => {
    if (!term.trim()) return;
    
    setLoading(true);
    try {
      const searchResponse = await fetch(
        createApiUrl(`snippets/search?search_term=${encodeURIComponent(term)}`),
        {
          headers: { ...getAuthHeaders() }
        }
      );
      if (searchResponse.ok) {
        const searchData = await searchResponse.json();
        setSearchResults(searchData);
        setSelectedIndex(0);
      } else {
        showError(t('report.snippets.searchError'), 'ERR-SNIPPET-003');
        setSearchResults([]);
      }
    } catch (error) {
      console.error('Error searching snippets:', error);
      showError(t('report.snippets.searchError'), 'ERR-SNIPPET-004');
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSnippetClick = async (snippet) => {
    try {
      // Increment usage counter
      const response = await fetch(createApiUrl(`snippets/${snippet.id}/use`), {
        method: 'POST',
        headers: { ...getAuthHeaders() }
      });
      
      if (!response.ok) {
        console.error('Error incrementing snippet usage: HTTP', response.status);
        // Nicht kritisch - Snippet wird trotzdem eingefügt, nur Counter nicht erhöht
      }
    } catch (error) {
      console.error('Error incrementing snippet usage:', error);
      // Nicht kritisch - Snippet wird trotzdem eingefügt
    }

    // Call parent callback with snippet
    if (onSnippetSelect) {
      onSnippetSelect(snippet);
    }
  };

  const handleKeyDown = (e) => {
    const displayedSnippets = getDisplayedSnippets();
    
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, displayedSnippets.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (displayedSnippets[selectedIndex]) {
        handleSnippetClick(displayedSnippets[selectedIndex]);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  const getDisplayedSnippets = () => {
    if (searchTerm.trim()) {
      return searchResults;
    }
    
    // Show most used + other snippets
    const otherSnippets = allSnippets.filter(
      snippet => !mostUsedSnippets.some(most => most.id === snippet.id)
    );
    return [...mostUsedSnippets, ...otherSnippets];
  };

  const formatSnippetPreview = (content) => {
    if (!content) return '';
    // Show first 80 characters with ellipsis
    return content.length > 80 ? `${content.substring(0, 80)}...` : content;
  };

  const displayedSnippets = getDisplayedSnippets();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      disableAutoFocus={false}
      disableEnforceFocus={false}
      disableRestoreFocus={true}
      PaperProps={{
        sx: {
          position: 'fixed',
          top: cursorPosition?.y || '50%',
          left: cursorPosition?.x || '50%',
          transform: cursorPosition ? 'translate(-50%, -100%)' : 'translate(-50%, -50%)',
          margin: 0,
          maxHeight: '400px'
        }
      }}
      onKeyDown={handleKeyDown}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SnippetIcon />
          <Typography variant="h6">
            {t('report.snippets.selectSnippet')}
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ p: 0 }}>
        {/* Search Field */}
        <Box sx={{ p: 2, pb: 1 }}>
          <TextField
            ref={searchRef}
            fullWidth
            size="small"
            autoFocus
            placeholder={t('report.snippets.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: loading && (
                <InputAdornment position="end">
                  <CircularProgress size={20} />
                </InputAdornment>
              )
            }}
          />
        </Box>

        {/* Snippets List */}
        <Box sx={{ maxHeight: '300px', overflow: 'auto' }} ref={listRef}>
          {displayedSnippets.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">
                {searchTerm.trim() 
                  ? t('report.snippets.noSearchResults')
                  : t('report.snippets.noSnippetsAvailable')
                }
              </Typography>
            </Box>
          ) : (
            <List dense>
              {/* Most Used Section (only when not searching) */}
              {!searchTerm.trim() && mostUsedSnippets.length > 0 && (
                <>
                  <ListItem sx={{ px: 2, py: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <StarIcon fontSize="small" color="primary" />
                      <Typography variant="caption" color="primary" fontWeight="bold">
                        {t('report.snippets.mostUsed')}
                      </Typography>
                    </Box>
                  </ListItem>
                  {mostUsedSnippets.map((snippet, index) => (
                    <ListItemButton
                      key={`most-${snippet.id}`}
                      selected={selectedIndex === index}
                      onClick={() => handleSnippetClick(snippet)}
                      sx={{ px: 3 }}
                    >
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" fontWeight="bold">
                              @{snippet.label}
                            </Typography>
                            <Chip 
                              label={`${snippet.used_x_times}x`} 
                              size="small" 
                              color="primary" 
                              variant="outlined"
                            />
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.primary">
                              {snippet.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatSnippetPreview(snippet.content)}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItemButton>
                  ))}
                  
                  {/* Divider between most used and all snippets */}
                  {allSnippets.length > mostUsedSnippets.length && (
                    <>
                      <Divider sx={{ my: 1 }} />
                      <ListItem sx={{ px: 2, py: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          {t('report.snippets.allSnippets')}
                        </Typography>
                      </ListItem>
                    </>
                  )}
                </>
              )}

              {/* Other/Search Results */}
              {(searchTerm.trim() ? searchResults : allSnippets.filter(
                snippet => !mostUsedSnippets.some(most => most.id === snippet.id)
              )).map((snippet, index) => {
                const adjustedIndex = searchTerm.trim() ? index : mostUsedSnippets.length + index;
                return (
                  <ListItemButton
                    key={snippet.id}
                    selected={selectedIndex === adjustedIndex}
                    onClick={() => handleSnippetClick(snippet)}
                    sx={{ px: 3 }}
                  >
                    <ListItemText
                      primary={
                        <Typography variant="body2" fontWeight="bold">
                          @{snippet.label}
                        </Typography>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.primary">
                            {snippet.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatSnippetPreview(snippet.content)}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItemButton>
                );
              })}
            </List>
          )}
        </Box>

        {/* Keyboard Hints */}
        <Box sx={{ p: 2, pt: 1, backgroundColor: 'action.hover' }}>
          <Typography variant="caption" color="text.secondary">
            {t('report.snippets.keyboardHints')}
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default SnippetSelector;
